import React, { useState, useRef } from 'react';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { auth } from '../firebase';
import { useNavigate, Link } from 'react-router-dom';
import ReCAPTCHA from 'react-google-recaptcha';
import { motion } from 'framer-motion';

function Login({ loadingBarRef }) { // Accept loadingBarRef as prop
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const navigate = useNavigate();
  const [error, setError] = useState('');
  const [captchaValue, setCaptchaValue] = useState(null);
  const recaptchaRef = useRef();
  const [buttonText, setButtonText] = useState('Login');

  const handleLogin = async (e) => {
    e.preventDefault();
    setButtonText('Logging in...');
    if (loadingBarRef.current) {
      loadingBarRef.current.continuousStart();
    }
    setTimeout(async () => {
      setButtonText('Login');
      if (loadingBarRef.current) {
        loadingBarRef.current.complete();
      }

      if (!captchaValue) {
        setError("Please complete the captcha verification");
        return;
      }

      try {
        await signInWithEmailAndPassword(auth, email, password);
        navigate('/');
      } catch (err) {
        setError(err.message);
        recaptchaRef.current.reset();
        setCaptchaValue(null);
      }
    }, 2000); // 2-second delay
  };

  return (
    <motion.div 
      className="max-w-md mx-auto px-4 py-8"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <motion.h2 
        className="text-2xl font-bold mb-4"
        initial={{ x: -20 }}
        animate={{ x: 0 }}
        transition={{ delay: 0.2 }}
      >
        Login
      </motion.h2>
      {error && <motion.p 
        className="text-red-500"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
      >
        {error}
      </motion.p>}
      <motion.form 
        onSubmit={handleLogin} 
        className="space-y-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3 }}
      >
        <div>
          <label className="block text-gray-700">Email</label>
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full border rounded px-3 py-2"
          />
        </div>
        <div>
          <label className="block text-gray-700">Password</label>
          <input
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full border rounded px-3 py-2"
          />
        </div>

        <div className="flex justify-center my-4">
          <ReCAPTCHA
            ref={recaptchaRef}
            sitekey="6Leys3EqAAAAAEjkHcbcAjaoJ1Bf4o1xcG8W1Hv9"
            onChange={(value) => setCaptchaValue(value)}
            size="normal"
            className="transform scale-90 sm:scale-100"
          />
        </div>

        <button 
          type="submit" 
          className={`w-full bg-indigo-600 text-white px-4 py-2 rounded hover:bg-indigo-700 disabled:bg-indigo-400 transition-transform duration-300 ${
            !captchaValue ? 'cursor-not-allowed' : 'transform hover:scale-105'
          }`}
          disabled={!captchaValue}
        >
          {buttonText}
        </button>
      </motion.form>
      <div className="mt-4 text-center">
        <Link to="/forgot-password" className="text-indigo-600 hover:underline">Forgot Password?</Link>
      </div>
      <div className="mt-2 text-center">
        <span>Don't have an account? </span>
        <Link to="/register" className="text-indigo-600 hover:underline">Register</Link>
      </div>
    </motion.div>
  );
}

export default Login;
